import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { IMAGES } from "@/data/content";

const TECHNIQUES = [
  { name: "Design First", detail: "Every cake starts as a sketch — colours, proportions and details planned before any baking begins." },
  { name: "Simple Flavours, Done Well", detail: "Vanilla or chocolate sponge with Nutella, pistachio or Lotus Biscoff filling — made fresh for each order." },
  { name: "Made for You", detail: "Names, ages, dates and favourite themes worked into the design so no two cakes are alike." },
  { name: "Allergen Aware", detail: "Allergens listed for every category and discussed with you before your order is confirmed." },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, delay, ease: "easeOut" },
});

export default function Story() {
  const navigate = useNavigate();

  return (
    <main data-testid="founder-story-page">
      <Navbar />

      <section className="relative overflow-hidden pb-24 pt-32 lg:pt-40">
        <div aria-hidden className="pointer-events-none absolute -right-32 top-0 h-[30rem] w-[30rem] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <motion.button
            data-testid="story-back-button"
            onClick={() => navigate("/")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-10 flex items-center gap-2 text-sm tracking-wide text-[#67718A] transition-colors hover:text-[#D4AF37]"
          >
            <ArrowLeft size={15} /> Back to home
          </motion.button>

          <div className="grid items-stretch gap-16 lg:grid-cols-2">
            <div>
              <motion.p
                {...fadeUp()}
                className="flex items-center gap-4 text-xs uppercase tracking-[0.32em] text-[#D4AF37]"
              >
                <span className="h-px w-12 bg-[#D4AF37]/60" /> The Founder
              </motion.p>
              <h1 className="mt-6 font-display text-4xl leading-[1.1] tracking-tight text-[#1B3A6B] sm:text-5xl lg:text-6xl">
                <span className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  >
                    Meet <span className="italic text-[#D4AF37]">Thara</span>
                  </motion.span>
                </span>
              </h1>
              <motion.div {...fadeUp(0.35)} className="mt-8 space-y-5 text-base leading-relaxed text-[#2F4A78]">
                <h2 className="font-display text-2xl italic text-[#8A6D1F]">From Design to Cake</h2>
                <p>I've always loved creating things that feel personal.</p>
                <p>
                  With a background in architecture and design, I've always been drawn to the
                  process of turning an idea into something tangible — thinking about form,
                  colour, proportion, detail and how all the little elements come together to
                  create something beautiful.
                </p>
                <p>That love for design eventually found a new canvas: cake.</p>
                <p>
                  What began as a passion for baking grew into a desire to create cakes that are
                  more than just something beautiful on a celebration table. I wanted each cake
                  to feel like it belonged to the person it was made for.
                </p>
                <p>
                  At Tarah, I bring together my love of design and my passion for baking to create
                  bespoke cakes inspired by you — your ideas, your personality, your story and the
                  moments you want to celebrate.
                </p>
                <p>
                  From the overall design to the smallest detail, every element is thoughtfully
                  created to make your cake feel truly one of a kind.
                </p>
                <p>Because the little moments we celebrate become the memories we keep.</p>
                <p>
                  And if I can create something that makes you pause, smile and think, "This was
                  made just for me," then that is the sweetest part of what I do.
                </p>
                <p className="font-display text-lg italic text-[#1B3A6B]">
                  Welcome to Tarah — Cake Design Studio.
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex"
            >
              <div className="absolute -right-3 -top-3 h-full w-full rounded-sm border border-[#D4AF37]/40" aria-hidden />
              <img
                data-testid="story-founder-image"
                src={IMAGES.founder}
                alt="Thara, founder of Tarah Cake Design Studio"
                className="relative aspect-[4/5] w-full rounded-sm object-cover lg:aspect-auto lg:h-full"
              />
              <div className="gold-glow absolute -bottom-7 -right-4 rounded-sm border border-[#D4AF37]/30 bg-[#FFFFFF]/95 px-6 py-4 backdrop-blur-xl sm:-right-8">
                <p className="font-label text-[10px] tracking-[0.32em] text-[#9A7B24]">FOUNDER & CAKE DESIGNER</p>
                <p className="mt-1 font-display text-xl italic text-[#8A6D1F]">Thara</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-y border-[#D4AF37]/15 bg-[#F7F2E8]/60 py-20">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <motion.blockquote {...fadeUp()} data-testid="story-quote">
            <p className="font-display text-2xl italic leading-relaxed text-[#8A6D1F] sm:text-3xl">
              “Tarah is my journey from designing spaces to designing edible moments —
              but it is your stories, celebrations and dreams that give this journey its soul.”
            </p>
            <footer className="mt-6 font-label text-xs tracking-[0.34em] text-[#D4AF37]">— THARA</footer>
          </motion.blockquote>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <motion.p {...fadeUp()} className="flex items-center justify-center gap-4 text-xs uppercase tracking-[0.32em] text-[#D4AF37]">
          <span className="h-px w-10 bg-[#D4AF37]/50" />
          How I Work
          <span className="h-px w-10 bg-[#D4AF37]/50" />
        </motion.p>
        <motion.h2 {...fadeUp(0.1)} className="mt-4 text-center font-display text-3xl tracking-tight text-[#1B3A6B] sm:text-4xl">
          The Tarah Approach
        </motion.h2>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TECHNIQUES.map((t, i) => (
            <motion.div
              key={t.name}
              {...fadeUp(i * 0.1)}
              data-testid={`story-technique-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-sm border border-[#D4AF37]/20 bg-[#FFFFFF]/60 p-7 backdrop-blur-xl transition-colors duration-500 hover:border-[#D4AF37]/50"
            >
              <p className="font-display text-4xl italic text-[#D4AF37]/40">0{i + 1}</p>
              <h3 className="mt-4 font-display text-xl text-[#8A6D1F]">{t.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#67718A]">{t.detail}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp(0.2)} className="mt-16 text-center">
          <button
            data-testid="story-book-consultation-button"
            onClick={() => navigate("/", { state: { scrollTo: "#enquiry" } })}
            className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-9 py-4 text-sm font-semibold tracking-wide text-[#1B3A6B] transition-all duration-300 hover:bg-[#8A6D1F]"
          >
            Send Tarah an Enquiry
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </section>

      <Footer />
      <WhatsAppButton />
    </main>
  );
}
